'use server'

import { apiRequest, ApiResponse } from '../request'

export type SngpcMovementType =
    | 'SNGPC_PURCHASE'
    | 'SNGPC_SALE'
    | 'SNGPC_LOSS'
    | 'SNGPC_THEFT'
    | 'SNGPC_TRANSFORM'
    | 'SNGPC_RETURN'

export type SngpcTransmissionStatus =
    | 'SNGPC_PENDING'
    | 'SNGPC_GENERATED'
    | 'SNGPC_TRANSMITTED'
    | 'SNGPC_ACCEPTED'
    | 'SNGPC_REJECTED'

export interface SngpcConfiguration {
    id: number
    companyId: number
    cnes: string
    anvsCnpj: string
    anvsResponsavel: string
    anvsCrf: string
    autoGenerate: boolean
    generateDay: number
    emailNotification: boolean
    notificationEmail?: string
    createdAt: string
    updatedAt: string
}

export interface SngpcMovement {
    id: number
    companyId: number
    medicationId: number
    batchId?: number
    type: SngpcMovementType
    quantity: number
    invoiceNumber?: string
    invoiceDate?: string
    supplierCnpj?: string
    patientName?: string
    patientDocument?: string
    prescriberName?: string
    prescriberRegistry?: string
    prescriptionDate?: string
    transmissionBatchId?: number
    createdAt: string
    medication?: {
        id: number
        name: string
        anvisaRegistry?: string
    }
    batch?: {
        id: number
        batchNumber: string
    }
}

export interface SngpcTransmissionBatch {
    id: number
    companyId: number
    referenceMonth: number
    referenceYear: number
    status: SngpcTransmissionStatus
    totalMovements: number
    xmlContent?: string
    protocolNumber?: string
    transmittedAt?: string
    responseMessage?: string
    createdAt: string
    updatedAt: string
}

export interface SngpcBalance {
    medicationId: number
    medication: {
        id: number
        name: string
        anvisaRegistry?: string
        controlType: string
    }
    initialBalance: number
    entries: number
    exits: number
    currentBalance: number
}

// Configuration
export async function getSngpcConfiguration(): Promise<
    ApiResponse<SngpcConfiguration | null>
> {
    return apiRequest<SngpcConfiguration | null>('stock/sngpc/config', {})
}

export async function updateSngpcConfiguration(data: {
    cnes?: string
    anvsCnpj?: string
    anvsResponsavel?: string
    anvsCrf?: string
    autoGenerate?: boolean
    generateDay?: number
    emailNotification?: boolean
    notificationEmail?: string
}): Promise<ApiResponse<SngpcConfiguration>> {
    return apiRequest<SngpcConfiguration>(
        'stock/sngpc/config',
        { data },
        'patch'
    )
}

// Movements
export async function getSngpcMovements(filters?: {
    medicationId?: number
    type?: SngpcMovementType
    startDate?: string
    endDate?: string
    pending?: boolean
}): Promise<ApiResponse<SngpcMovement[]>> {
    return apiRequest<SngpcMovement[]>('stock/sngpc/movements', {
        params: filters,
    })
}

export async function getSngpcMovement(
    id: number
): Promise<ApiResponse<SngpcMovement>> {
    return apiRequest<SngpcMovement>(`stock/sngpc/movements/${id}`, {})
}

export async function createSngpcMovement(data: {
    medicationId: number
    batchId?: number
    type: SngpcMovementType
    quantity: number
    invoiceNumber?: string
    invoiceDate?: string
    supplierCnpj?: string
    patientName?: string
    patientDocument?: string
    prescriberName?: string
    prescriberRegistry?: string
    prescriptionDate?: string
}): Promise<ApiResponse<SngpcMovement>> {
    return apiRequest<SngpcMovement>('stock/sngpc/movements', { data }, 'post')
}

// Transmission Batches
export async function getSngpcBatches(): Promise<
    ApiResponse<SngpcTransmissionBatch[]>
> {
    return apiRequest<SngpcTransmissionBatch[]>('stock/sngpc/batches', {})
}

export async function generateSngpcBatch(
    month: number,
    year: number
): Promise<ApiResponse<SngpcTransmissionBatch>> {
    return apiRequest<SngpcTransmissionBatch>(
        'stock/sngpc/batches/generate',
        { data: { month, year } },
        'post'
    )
}

export async function transmitSngpcBatch(
    id: number
): Promise<ApiResponse<SngpcTransmissionBatch>> {
    return apiRequest<SngpcTransmissionBatch>(
        `stock/sngpc/batches/${id}/transmit`,
        {},
        'post'
    )
}

export async function downloadSngpcXml(id: number): Promise<ApiResponse<Blob>> {
    return apiRequest<Blob>(`stock/sngpc/batches/${id}/xml`, {})
}

// Balance
export async function getSngpcBalance(): Promise<ApiResponse<SngpcBalance[]>> {
    return apiRequest<SngpcBalance[]>('stock/sngpc/balance', {})
}

export async function getSngpcMedicationBalance(
    medicationId: number
): Promise<ApiResponse<SngpcBalance>> {
    return apiRequest<SngpcBalance>(
        `stock/sngpc/balance/${medicationId}`,
        {}
    )
}

// Reports
export async function getSngpcReport(
    month: number,
    year: number
): Promise<ApiResponse<any>> {
    return apiRequest<any>('stock/sngpc/report', { params: { month, year } })
}

// Labels
export const SNGPC_MOVEMENT_TYPE_LABELS: Record<SngpcMovementType, string> = {
    SNGPC_PURCHASE: 'Compra',
    SNGPC_SALE: 'Venda',
    SNGPC_LOSS: 'Perda',
    SNGPC_THEFT: 'Furto/Roubo',
    SNGPC_TRANSFORM: 'Transformacao',
    SNGPC_RETURN: 'Devolucao',
}

export const SNGPC_TRANSMISSION_STATUS_LABELS: Record<
    SngpcTransmissionStatus,
    string
> = {
    SNGPC_PENDING: 'Pendente',
    SNGPC_GENERATED: 'Gerado',
    SNGPC_TRANSMITTED: 'Transmitido',
    SNGPC_ACCEPTED: 'Aceito',
    SNGPC_REJECTED: 'Rejeitado',
}

export const SNGPC_TRANSMISSION_STATUS_COLORS: Record<
    SngpcTransmissionStatus,
    string
> = {
    SNGPC_PENDING: 'badge-ghost',
    SNGPC_GENERATED: 'badge-info',
    SNGPC_TRANSMITTED: 'badge-warning',
    SNGPC_ACCEPTED: 'badge-success',
    SNGPC_REJECTED: 'badge-error',
}
